import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { StoredBeauftragungPreviewDialog } from "@/components/beauftragung/StoredBeauftragungPreviewDialog";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SelectInput, TextInput, TextareaInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Dialog } from "@/components/ui/Dialog";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  completeTagesgeschaeftCandidate,
  dismissTagesgeschaeftCandidate,
  fetchTagesgeschaeftBeauftragung,
  getTagesgeschaeftProjectPrefill,
  isStoredMailBeauftragung,
  listTagesgeschaeftCandidates,
  patchTagesgeschaeftCandidateFields,
  reanalyzeTagesgeschaeftCandidate,
  tagesgeschaeftAttachmentUrl,
  type TagesgeschaeftCandidate,
  type TagesgeschaeftCandidateStatus,
} from "@/lib/tagesgeschaeft";
import { useNewProject } from "@/pages/projekte/useNewProject";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";

type StatusFilter = TagesgeschaeftCandidateStatus | "all";

const POLL_MS = 20_000;

const EDITABLE_FIELD_KEYS = [
  "beschreibung",
  "ansprechpartner",
  "ansprechpartner_email",
  "adresse",
  "ort_zusatz",
  "beauftragung_source",
  "beauftragung_attachment_hint",
] as const;

const FIELD_LABELS: Record<string, string> = {
  beschreibung: "Beschreibung",
  ansprechpartner: "Ansprechpartner",
  ansprechpartner_email: "Ansprechpartner E-Mail",
  adresse: "Adresse / Ort",
  ort_zusatz: "Ort-Zusatz",
  beauftragung_source: "Beauftragung-Quelle",
  beauftragung_attachment_hint: "Beauftragung-Anhang",
};

function statusBadge(status: TagesgeschaeftCandidateStatus): string {
  switch (status) {
    case "pending":
      return "bg-status-orange/15 text-status-orange";
    case "confirmed":
      return "bg-status-green/15 text-status-green";
    case "dismissed":
      return "bg-label-secondary/15 text-label-secondary";
    default:
      return "bg-label-secondary/15 text-label-secondary";
  }
}

function statusLabel(status: TagesgeschaeftCandidateStatus): string {
  switch (status) {
    case "pending":
      return "Offen";
    case "confirmed":
      return "Erledigt";
    case "dismissed":
      return "Verworfen";
    default:
      return status;
  }
}

function senderLabel(row: TagesgeschaeftCandidate): string {
  const name = (row.from_name || "").trim();
  const email = (row.from_email || "").trim();
  if (name && email) return `${name} <${email}>`;
  return name || email || "—";
}

export function TagesgeschaeftIndexPage() {
  const { open: openNewProject } = useNewProject();
  const [items, setItems] = useState<TagesgeschaeftCandidate[]>([]);
  const [counts, setCounts] = useState({ pending: 0, confirmed: 0, dismissed: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liveMonitor, setLiveMonitor] = useState(true);
  const [aiEnabled, setAiEnabled] = useState(false);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRow, setReviewRow] = useState<TagesgeschaeftCandidate | null>(null);
  const [previewRow, setPreviewRow] = useState<TagesgeschaeftCandidate | null>(null);
  const [editFields, setEditFields] = useState<Record<string, string>>({});
  const [selectedAttId, setSelectedAttId] = useState<string>("");
  const [savingFields, setSavingFields] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const res = await listTagesgeschaeftCandidates(
          statusFilter === "all" ? undefined : statusFilter,
        );
        setItems(res.items || []);
        setCounts(
          res.counts || {
            pending: 0,
            confirmed: 0,
            dismissed: 0,
            total: res.items?.length ?? 0,
          },
        );
        setLiveMonitor(res.live_monitor ?? true);
        setAiEnabled(res.ai_enabled ?? false);
      } catch (e) {
        const msg =
          e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Laden fehlgeschlagen.";
        if (!silent) setError(msg);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [statusFilter],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = window.setInterval(() => {
      // Versteckter Tab: kein Hintergrund-Polling (Serverlast).
      if (document.visibilityState === "hidden") return;
      void load(true);
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const openReview = (row: TagesgeschaeftCandidate) => {
    setReviewRow(row);
    const fields = { ...(row.extracted_fields || {}) };
    const next: Record<string, string> = {};
    for (const key of EDITABLE_FIELD_KEYS) {
      const v = fields[key];
      next[key] = v == null ? "" : String(v);
    }
    setEditFields(next);
    setSelectedAttId(String(fields._beauftragung_attachment_id || ""));
    setReviewOpen(true);
  };

  const saveFields = async () => {
    if (!reviewRow) return;
    setSavingFields(true);
    try {
      const payload: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(editFields)) {
        payload[k] = v.trim() || null;
      }
      if (selectedAttId) payload._beauftragung_attachment_id = selectedAttId;
      const updated = await patchTagesgeschaeftCandidateFields(reviewRow.id, payload);
      setReviewRow(updated);
      toast.success("Felder gespeichert.");
      await load(true);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingFields(false);
    }
  };

  const handleDismiss = async (row: TagesgeschaeftCandidate) => {
    setBusyId(row.id);
    try {
      await dismissTagesgeschaeftCandidate(row.id);
      toast.success("Vorschlag verworfen.");
      setReviewOpen(false);
      await load(true);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Verwerfen fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReanalyze = async (row: TagesgeschaeftCandidate) => {
    setBusyId(row.id);
    try {
      const updated = await reanalyzeTagesgeschaeftCandidate(row.id);
      toast.success("Erneut analysiert.");
      if (reviewOpen) openReview(updated);
      await load(true);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Analyse fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const handleFreigabe = async (row: TagesgeschaeftCandidate) => {
    setBusyId(row.id);
    try {
      // Aktuelle Felder sichern, bevor der Projekt-Dialog öffnet
      if (reviewOpen && reviewRow?.id === row.id) {
        const payload: Record<string, string | null> = {};
        for (const [k, v] of Object.entries(editFields)) {
          payload[k] = v.trim() || null;
        }
        if (selectedAttId) payload._beauftragung_attachment_id = selectedAttId;
        await patchTagesgeschaeftCandidateFields(row.id, payload);
      }

      const prefill = await getTagesgeschaeftProjectPrefill(row.id);
      setReviewOpen(false);

      openNewProject({
        ordnerPrefill: {
          type: "projekt",
          ordnerNummer: String(prefill.ordner_prefill.ordnerNummer || ""),
          jahr: prefill.ordner_prefill.jahr,
          kunde: prefill.ordner_prefill.kunde || undefined,
        },
        formPrefill: {
          beschreibung: prefill.form_prefill.beschreibung,
          ansprechpartner: prefill.form_prefill.ansprechpartner,
          ansprechpartnerEmail: prefill.form_prefill.ansprechpartner_email,
          adresse: prefill.form_prefill.adresse,
          ortZusatz: prefill.form_prefill.ort_zusatz,
        },
        // Beauftragung erfolgt über Anhang-Upload – keine mündliche Nachfrage.
        skipAskCommissionAfterCreate: true,
        onCreated: (order) => {
          void (async () => {
            try {
              const res = await completeTagesgeschaeftCandidate(row.id, {
                auftrag_id: order.id,
                beauftragung_source: editFields.beauftragung_source || "auto",
                selected_attachment_id: selectedAttId || null,
              });
              toast.success(
                res.beauftragung?.filename
                  ? `Projekt angelegt, Beauftragung „${res.beauftragung.filename}“ abgelegt.`
                  : "Projekt angelegt und Beauftragung abgelegt.",
              );
              await load(true);
            } catch (e) {
              toast.error(
                e instanceof ApiError
                  ? e.message
                  : "Projekt wurde angelegt, Beauftragung-Ablage fehlgeschlagen.",
              );
              await load(true);
            }
          })();
        },
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Freigabe fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const attachments = useMemo(() => {
    const list = reviewRow?.attachments_summary;
    return Array.isArray(list) ? list.filter((a) => a?.id) : [];
  }, [reviewRow]);

  const loadPreview = useCallback(() => {
    if (!previewRow) return Promise.reject(new Error("Keine Beauftragung ausgewählt."));
    return fetchTagesgeschaeftBeauftragung(previewRow.id);
  }, [previewRow]);

  return (
    <AppShell
      title="Tagesgeschäft"
      subtitle={`Eingehende Kunden-Mails analysieren und als Projekt freigeben${
        liveMonitor ? " · Live-Überwachung aktiv" : ""
      }${!aiEnabled ? " · KI aus (Heuristik)" : ""}`}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["pending", "Offen", counts.pending],
              ["confirmed", "Erledigt", counts.confirmed],
              ["dismissed", "Verworfen", counts.dismissed],
              ["all", "Alle", counts.total],
            ] as const
          ).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key)}
              className={
                statusFilter === key
                  ? "rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-white"
                  : "rounded-full bg-fill-tertiary px-3 py-1.5 text-sm font-medium text-label-secondary hover:bg-fill-secondary"
              }
            >
              {label} ({count})
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => void load()}>
            Aktualisieren
          </Button>
          <Link
            to="/admin/einstellungen/tagesgeschaeft"
            className="inline-flex items-center rounded-full bg-fill-tertiary px-3 py-1.5 text-sm font-medium text-label-secondary hover:bg-fill-secondary"
          >
            Postfächer einstellen
          </Link>
        </div>
      </div>

      {error ? (
        <Alert variant="error" title="Fehler" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Laden …
        </div>
      ) : items.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-label-secondary">
            Keine Mails in diesem Filter. Postfächer unter Einstellungen → Tagesgeschäft
            konfigurieren und ggf. einen Scan starten.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((row) => (
            <Card key={row.id} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusBadge(row.status)}`}>
                      {statusLabel(row.status)}
                    </span>
                    <span className="text-xs text-label-tertiary">
                      {row.received_at ? fmtDateTime(row.received_at) : "—"}
                    </span>
                    {row.has_attachments ? (
                      <span className="text-xs text-label-secondary">
                        {row.attachments_summary?.length || 0} Anhang
                      </span>
                    ) : null}
                  </div>
                  <h3 className="truncate text-base font-semibold text-label">
                    {row.subject || "(ohne Betreff)"}
                  </h3>
                  <p className="text-sm text-label-secondary">{senderLabel(row)}</p>
                  <p className="text-xs text-label-tertiary">
                    {[row.mailbox_label || row.mailbox, row.ordner_nummer, row.kunden_name]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {row.extracted_fields?.beschreibung ? (
                    <p className="mt-1 text-sm text-label">
                      {String(row.extracted_fields.beschreibung)}
                    </p>
                  ) : row.snippet ? (
                    <p className="mt-1 line-clamp-2 text-sm text-label-secondary">{row.snippet}</p>
                  ) : null}
                  {row.status === "confirmed" && row.created_auftrag_id ? (
                    <p className="text-xs text-status-green">
                      Projekt #{row.created_auftrag_id}
                      {row.beauftragung_filename ? ` · ${row.beauftragung_filename}` : ""}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {row.status === "pending" ? (
                    <>
                      <Button type="button" size="sm" onClick={() => openReview(row)}>
                        Prüfen / Freigeben
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={busyId === row.id}
                        onClick={() => void handleReanalyze(row)}
                      >
                        Neu analysieren
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        disabled={busyId === row.id}
                        onClick={() => void handleDismiss(row)}
                      >
                        Verwerfen
                      </Button>
                    </>
                  ) : (
                    <>
                      {isStoredMailBeauftragung(row.beauftragung_filename) ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setPreviewRow(row)}
                        >
                          Beauftragung ansehen
                        </Button>
                      ) : null}
                      <Button type="button" size="sm" variant="secondary" onClick={() => openReview(row)}>
                        Details
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        title={reviewRow?.subject || "Mail prüfen"}
        size="lg"
        footer={
          reviewRow?.status === "pending" ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={savingFields}
                onClick={() => void saveFields()}
              >
                {savingFields ? "Speichern …" : "Felder speichern"}
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={busyId === reviewRow.id}
                onClick={() => void handleDismiss(reviewRow)}
              >
                Verwerfen
              </Button>
              <Button
                type="button"
                disabled={busyId === reviewRow.id}
                onClick={() => void handleFreigabe(reviewRow)}
              >
                Freigeben → Projekt anlegen
              </Button>
            </div>
          ) : undefined
        }
      >
        {reviewRow ? (
          <div className="space-y-4">
            <div className="text-sm text-label-secondary">
              <div>{senderLabel(reviewRow)}</div>
              <div>
                {[reviewRow.mailbox, reviewRow.ordner_nummer, reviewRow.kunden_name]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {reviewRow.snippet ? (
                <p className="mt-2 whitespace-pre-wrap rounded-ios bg-fill-tertiary p-3 text-label">
                  {reviewRow.snippet}
                </p>
              ) : null}
            </div>

            {reviewRow.status === "pending" ? (
              <div className="grid gap-3 md:grid-cols-2">
                {EDITABLE_FIELD_KEYS.map((key) =>
                  key === "beschreibung" || key === "adresse" ? (
                    <TextareaInput
                      key={key}
                      label={FIELD_LABELS[key] || key}
                      value={editFields[key] || ""}
                      onChange={(e) =>
                        setEditFields((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      rows={key === "beschreibung" ? 3 : 2}
                    />
                  ) : key === "beauftragung_source" ? (
                    <SelectInput
                      key={key}
                      label={FIELD_LABELS[key] || key}
                      value={editFields[key] || "auto"}
                      onChange={(e) =>
                        setEditFields((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    >
                      <option value="auto">Auto (Anhang wenn vorhanden)</option>
                      <option value="attachment">Anhang</option>
                      <option value="mail_body">Mailinhalt</option>
                    </SelectInput>
                  ) : (
                    <TextInput
                      key={key}
                      label={FIELD_LABELS[key] || key}
                      value={editFields[key] || ""}
                      onChange={(e) =>
                        setEditFields((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    />
                  ),
                )}
              </div>
            ) : (
              <>
              {isStoredMailBeauftragung(reviewRow.beauftragung_filename) ? (
                <Button type="button" size="sm" onClick={() => setPreviewRow(reviewRow)}>
                  Beauftragung ansehen
                </Button>
              ) : null}
              <dl className="grid gap-2 text-sm md:grid-cols-2">
                {EDITABLE_FIELD_KEYS.map((key) => (
                  <div key={key}>
                    <dt className="text-xs text-label-tertiary">{FIELD_LABELS[key]}</dt>
                    <dd className="text-label">
                      {reviewRow.extracted_fields?.[key] != null
                        ? String(reviewRow.extracted_fields[key])
                        : "—"}
                    </dd>
                  </div>
                ))}
              </dl>
              </>
            )}

            {attachments.length > 0 ? (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-label">Anhänge</h4>
                <ul className="space-y-1 text-sm">
                  {attachments.map((att) => (
                    <li key={String(att.id)} className="flex flex-wrap items-center gap-2">
                      {reviewRow.status === "pending" ? (
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="beauftragung-att"
                            checked={selectedAttId === String(att.id)}
                            onChange={() => setSelectedAttId(String(att.id))}
                          />
                          <span>{att.name || att.id}</span>
                        </label>
                      ) : (
                        <span>{att.name || att.id}</span>
                      )}
                      <a
                        className="text-accent underline"
                        href={tagesgeschaeftAttachmentUrl(reviewRow.id, String(att.id))}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Öffnen
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-label-tertiary">
                  Markierter Anhang wird als Beauftragung im neuen Projekt abgelegt (sonst Mailinhalt).
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Dialog>
      <StoredBeauftragungPreviewDialog
        open={previewRow != null}
        title={previewRow?.subject || previewRow?.beauftragung_filename || "Beauftragung"}
        description={previewRow?.beauftragung_filename}
        load={loadPreview}
        onClose={() => setPreviewRow(null)}
      />
    </AppShell>
  );
}
